import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import request from 'shared/lib/request';
import { dispatchReportError, dispatchReportErrorKey, extractResponseCode } from './reportErrorToast';
import {
  validationQueryKeys,
  XbrlValidationRunPlain,
  LatestValidation,
  ValidationResult,
  ValidationStatus,
  CategoryResult,
  Severity,
  ValidationFinding,
  ValidationCategory,
} from 'app/api/validation-queries';

export type { ValidationResult, ValidationStatus, CategoryResult, Severity, ValidationFinding, ValidationCategory };

export interface UseRunValidationInput {
  documentSuperId: string;
  documentRevisionId: string | undefined;
}

export function useRunValidation({ documentSuperId, documentRevisionId }: UseRunValidationInput) {
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<ValidationResult | null>(null);
  const queryClient = useQueryClient();

  const run = async () => {
    if (isRunning) return;
    if (!documentRevisionId) {
      dispatchReportErrorKey('validation');
      return;
    }
    setIsRunning(true);
    queryClient.setQueryData<LatestValidation>(validationQueryKeys.latest(documentSuperId), (prev) => ({
      latest: prev?.latest ?? null,
      stale: prev?.stale ?? false,
      running: { startedAt: new Date().toISOString(), triggeredByUserId: null },
    }));
    try {
      const url = `/gaby/validation/${documentSuperId}?documentRevisionId=${documentRevisionId}`;
      const res = await request.post(url, {});
      const persisted = res.data as XbrlValidationRunPlain;
      setResult(persisted.result);
      await queryClient.cancelQueries({ queryKey: validationQueryKeys.latest(documentSuperId) });
      queryClient.setQueryData<LatestValidation>(validationQueryKeys.latest(documentSuperId), { latest: persisted, stale: false, running: null });
    } catch (err) {
      if (extractResponseCode(err) !== 'CONFLICT') dispatchReportError('validation', err);
      await queryClient.invalidateQueries({ queryKey: validationQueryKeys.latest(documentSuperId) });
    } finally {
      setIsRunning(false);
    }
  };

  return { run, isRunning, result };
}
